import { useState, useMemo } from "react";
import { Car, Calendar, Edit3 } from "lucide-react";
import { getAllBrands, getModelsByBrand } from "../data/carBrands";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const allBrands = getAllBrands();

// Genera array di anni dal 1990 ad oggi
const currentYear = new Date().getFullYear();
const years = Array.from({ length: currentYear - 1989 }, (_, i) => currentYear - i);

export default function VehicleSelector({ 
  selectedBrand, 
  selectedModel,
  selectedYear,
  onBrandChange, 
  onModelChange,
  onYearChange,
  brandLabel = "Marca Veicolo",
  modelLabel = "Modello Veicolo",
  yearLabel = "Anno Immatricolazione",
  required = false,
  showYear = true
}) {
  const [showCustomBrand, setShowCustomBrand] = useState(false);
  const [showCustomModel, setShowCustomModel] = useState(false);
  const [showCustomYear, setShowCustomYear] = useState(false);

  // Calcola i modelli in base al brand selezionato - useMemo per performance
  const models = useMemo(() => {
    if (selectedBrand && !showCustomBrand && allBrands.includes(selectedBrand)) {
      return getModelsByBrand(selectedBrand);
    }
    return [];
  }, [selectedBrand, showCustomBrand]);

  // Determina se mostrare il custom model input
  const shouldShowCustomModel = useMemo(() => {
    if (showCustomModel) return true;
    if (showCustomBrand) return true;
    if (selectedModel && models.length > 0 && !models.includes(selectedModel)) return true;
    return false;
  }, [showCustomModel, showCustomBrand, selectedModel, models]);

  // Determina se l'anno è custom
  const shouldShowCustomYear = useMemo(() => {
    if (showCustomYear) return true;
    if (selectedYear && !years.includes(parseInt(selectedYear))) return true;
    return false;
  }, [showCustomYear, selectedYear]);

  const handleBrandSelect = (value) => {
    if (value === "__custom__") {
      setShowCustomBrand(true);
      setShowCustomModel(true);
      onBrandChange("");
      onModelChange("");
    } else {
      setShowCustomBrand(false);
      setShowCustomModel(false);
      onBrandChange(value);
      onModelChange("");
    }
  };

  const handleModelSelect = (value) => {
    if (value === "__custom__") {
      setShowCustomModel(true);
      onModelChange("");
    } else {
      setShowCustomModel(false);
      onModelChange(value);
    }
  };

  const handleYearSelect = (value) => {
    if (value === "__custom__") {
      setShowCustomYear(true);
      onYearChange("");
    } else {
      setShowCustomYear(false);
      onYearChange(value);
    }
  };

  return (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-12">
      {/* Brand Selection - 5 colonne */}
      <div className="form-group md:col-span-5">
        <label className="form-label flex items-center gap-2">
          <Car className="w-4 h-4 text-primary" />
          {brandLabel} {required && "*"}
        </label>
        {showCustomBrand ? (
          <div className="flex gap-2">
            <Input
              type="text"
              className="flex-1 bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
              placeholder="Inserisci marca"
              value={selectedBrand}
              onChange={(e) => onBrandChange(e.target.value)}
              required={required}
              data-testid="vehicle-brand-custom-input"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setShowCustomBrand(false);
                onBrandChange("");
              }}
              className="border-zinc-300 text-zinc-600 hover:bg-zinc-100"
            >
              Lista
            </Button>
          </div>
        ) : (
          <Select
            value={selectedBrand || ""}
            onValueChange={handleBrandSelect}
            required={required}
          >
            <SelectTrigger 
              className="w-full bg-white border-zinc-300 text-zinc-900 min-w-[200px]"
              data-testid="vehicle-brand-select"
            >
              <SelectValue placeholder="Seleziona marca" className="text-zinc-500" />
            </SelectTrigger>
            <SelectContent className="bg-white border-zinc-300 max-h-[400px] min-w-[250px]">
              {allBrands.map(brand => (
                <SelectItem 
                  key={brand} 
                  value={brand}
                  className="text-zinc-900 hover:bg-zinc-100 focus:bg-zinc-100 cursor-pointer"
                >
                  {brand}
                </SelectItem>
              ))}
              <SelectItem 
                value="__custom__"
                className="text-primary hover:bg-zinc-100 focus:bg-zinc-100 cursor-pointer border-t border-zinc-200 mt-1"
              >
                <span className="flex items-center gap-2">
                  <Edit3 className="w-3 h-3" />
                  Inserisci manualmente
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Model Selection - 5 colonne */}
      <div className="form-group md:col-span-5">
        <label className="form-label">{modelLabel} {required && "*"}</label>
        {shouldShowCustomModel ? (
          <div className="flex gap-2">
            <Input
              type="text"
              className="flex-1 bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
              placeholder="Inserisci modello"
              value={selectedModel}
              onChange={(e) => onModelChange(e.target.value)}
              required={required}
              data-testid="vehicle-model-custom-input"
            />
            {models.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowCustomModel(false);
                  onModelChange("");
                }}
                className="border-zinc-300 text-zinc-600 hover:bg-zinc-100"
              >
                Lista
              </Button>
            )}
          </div>
        ) : (
          <Select
            value={selectedModel || ""}
            onValueChange={handleModelSelect}
            disabled={!selectedBrand}
            required={required}
          >
            <SelectTrigger 
              className="w-full bg-white border-zinc-300 text-zinc-900 disabled:opacity-50 disabled:bg-zinc-100 min-w-[200px]"
              data-testid="vehicle-model-select"
            >
              <SelectValue placeholder={selectedBrand ? "Seleziona modello" : "Prima seleziona la marca"} className="text-zinc-500" />
            </SelectTrigger>
            <SelectContent className="bg-white border-zinc-300 max-h-[400px] min-w-[280px]">
              {models.map(model => (
                <SelectItem 
                  key={model} 
                  value={model}
                  className="text-zinc-900 hover:bg-zinc-100 focus:bg-zinc-100 cursor-pointer"
                >
                  {model}
                </SelectItem>
              ))}
              {models.length > 0 && (
                <SelectItem 
                  value="__custom__"
                  className="text-primary hover:bg-zinc-100 focus:bg-zinc-100 cursor-pointer border-t border-zinc-200 mt-1"
                >
                  <span className="flex items-center gap-2">
                    <Edit3 className="w-3 h-3" />
                    Inserisci manualmente
                  </span>
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Year Selection - 2 colonne */}
      {showYear && onYearChange && (
        <div className="form-group md:col-span-2">
          <label className="form-label flex items-center gap-2">
            <Calendar className="w-4 h-4 text-primary" />
            {yearLabel}
          </label>
          {shouldShowCustomYear ? (
            <div className="flex gap-2">
              <Input
                type="number"
                className="flex-1 bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                placeholder="es. 1985"
                value={selectedYear}
                onChange={(e) => onYearChange(e.target.value)}
                min="1900"
                max={currentYear}
                data-testid="vehicle-year-custom-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowCustomYear(false);
                  onYearChange("");
                }}
                className="border-zinc-300 text-zinc-600 hover:bg-zinc-100"
              >
                Lista
              </Button>
            </div>
          ) : (
            <Select
              value={selectedYear?.toString() || ""}
              onValueChange={handleYearSelect}
            >
              <SelectTrigger 
                className="w-full bg-white border-zinc-300 text-zinc-900"
                data-testid="vehicle-year-select"
              >
                <SelectValue placeholder="Seleziona anno" className="text-zinc-500" />
              </SelectTrigger>
              <SelectContent className="bg-white border-zinc-300 max-h-[300px]">
                {years.map(year => (
                  <SelectItem 
                    key={year} 
                    value={year.toString()}
                    className="text-zinc-900 hover:bg-zinc-100 focus:bg-zinc-100 cursor-pointer"
                  >
                    {year}
                  </SelectItem>
                ))}
                <SelectItem 
                  value="__custom__"
                  className="text-primary hover:bg-zinc-100 focus:bg-zinc-100 cursor-pointer border-t border-zinc-200 mt-1"
                >
                  <span className="flex items-center gap-2">
                    <Edit3 className="w-3 h-3" />
                    Inserisci manualmente
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      )}
    </div>
  );
}
